import { useAutoAnimate } from "@formkit/auto-animate/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FeeProfileType, PaymentGateway } from "@prisma/client";
import * as RadioGroup from "@radix-ui/react-radio-group";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import type { RouterOutputs } from "@calcom/trpc/react";
import { trpc } from "@calcom/trpc/react";
import { Button, Label, TextAreaField, TextField, Form, showToast, Switch } from "@calcom/ui";

import { defaultFinixMerchantFeeProfile } from "@lib/finix";
import { canSelectPmsPaymentType, getDefaultPmsPaymentTypeId } from "@lib/payment/nexhealthPaymentType";
import { formatAmountToTwoDecimals } from "@lib/practice/helper";

import { DollarIcon } from "@components/Icons";
import { AlertIcon } from "@components/Icons";
import { useOfficeContext } from "@components/navbar";
import NexhealthPaymentTypeSelect from "@components/patients/NexhealthPaymentTypeSelect";
import { OfficePatientSelect } from "@components/patients/OfficePatientSelect";
import PaymentSuccessDialog from "@components/patients/PaymentSuccessDialog";
import FamilyPaymentAllocation from "@components/patients/terminalPayment/FamilyPaymentAllocation";
import type { Allocation } from "@components/patients/terminalPayment/FamilyPaymentAllocation";
import { PatientStoredPaymentMethodSelect } from "@components/payment/PatientStoredPaymentMethodSelect";
import PaymentForm from "@components/payment/PaymentForm";
import PayrixForm from "@components/payment/PayrixForm";

type Props = {
  patientId?: number;
  officeId: number;
  appId: string;
  env: string;
  setPaymentDialog: React.Dispatch<React.SetStateAction<boolean>>;
  payrixApiKey: string;
  payrixPaymentEnv: string;
};

type FormValues = {
  cardId: number;
  amount: string;
  note: string;
  enableSurcharge: boolean;
  discountAmount: number;
  discountPercentage: number;
  patientId?: number;
  pmsPaymentTypeId?: string;
};

type Card = RouterOutputs["viewer"]["patients"]["getCardList"][number];

export default function VirtualPayment({
  patientId,
  officeId,
  appId,
  env,
  setPaymentDialog,
  payrixApiKey,
  payrixPaymentEnv,
}: Props) {
  const [patient, setPatient] = useState<number | undefined>(patientId);
  const [feeCharged, setFeeCharged] = useState(false);
  const [feeRate, setFeeRate] = useState(defaultFinixMerchantFeeProfile.cnpPercFee);
  const [processedAmount, setProcessedAmount] = useState(0);
  const [animationParentRef] = useAutoAnimate<HTMLDivElement>();
  const [loading, setLoading] = useState(false);
  const [paymentOption, setPaymentOption] = useState("add-new");
  const [paymentSuccessDialogOpen, setPaymentSuccessDialogOpen] = useState(false);
  const [card, setCard] = useState<Card | undefined>(undefined);
  const [isACHPayment, setIsACHPayment] = useState(false);
  const [splitEnabled, setSplitEnabled] = useState(false);
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const processingRef = useRef<boolean>(false);

  const form = useForm<FormValues>({
    defaultValues: {
      enableSurcharge: true,
      amount: "0.00",
      discountAmount: 0,
      discountPercentage: 0,
      patientId: patientId || undefined,
    },
    resolver: zodResolver(
      z.object({
        cardId: z.number(),
        amount: z
          .string()
          .regex(/^\d+\.\d{2}$/, { message: "Must have exactly 2 decimal places" })
          .refine((val) => parseFloat(val) > 0, { message: "Should be greater than 0" }),
        discountAmount: z.number().optional(),
        discountPercentage: z.number().optional(),
        note: z.string().optional(),
        enableSurcharge: z.boolean(),
        pmsPaymentTypeId: z.string().optional(),
      })
    ),
  });
  const { register, formState, watch, setValue, trigger, reset, getValues } = form;

  const watchAmount = watch("amount");

  const { office } = useOfficeContext();
  const showPaymentTypeSelect = canSelectPmsPaymentType(office);

  const { data: singlePatient } = trpc.viewer.patients.getById.useQuery(
    {
      billingOfficeId: officeId,
      patientId: Number(patient),
      _tag: "virtualPayment",
    },
    {
      enabled: !!patient,
      refetchInterval: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );

  const { data: familyData } = trpc.viewer.patients.getFamily.useQuery(
    {
      patientId: Number(patient),
      officeId,
    },
    {
      enabled: !!patient,
      refetchInterval: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );

  const familyMembers = useMemo(() => {
    if (!patient || !familyData?.members?.length) return [];
    if (!familyData.members.some((m) => m.patientId === patient)) return [];
    return familyData.members.map((member) => ({
      patientId: member.patientId,
      name: member.name,
      type: member.type,
      balance: member.balance,
    }));
  }, [familyData, patient]);

  useEffect(() => {
    if (singlePatient) {
      setValue("amount", formatAmountToTwoDecimals(singlePatient.amountDue));
    }
  }, [singlePatient]);

  useEffect(() => {
    setSplitEnabled(false);
    setAllocations([]);
  }, [patient]);

  useEffect(() => {
    if (office?.enableFamilyAllocation && familyMembers.length > 1) {
      setSplitEnabled(true);
    } else {
      setSplitEnabled(false);
      setAllocations([]);
    }
  }, [familyMembers, office?.enableFamilyAllocation]);

  useEffect(() => {
    if (singlePatient && singlePatient.cardList.length) {
      setPaymentOption("use-existing");
    } else {
      setPaymentOption("add-new");
    }
  }, [singlePatient]);

  useEffect(() => {
    if (office && card) {
      setValue(
        "enableSurcharge",
        (office.applySurchargeFee &&
          office.surchargeOnVirtualPay &&
          !isACHPayment &&
          card.cardType?.toLowerCase() === "credit") ||
          (office.enableDualPricing && !isACHPayment)
      );
    }
  }, [office, isACHPayment, card, setValue]);

  useEffect(() => {
    if (office) {
      setValue(
        "enableSurcharge",
        (office.applySurchargeFee && office.surchargeOnVirtualPay) || office.enableDualPricing
      );
      if (office.FinixMerchantFeeProfile) {
        setFeeRate(
          office.FinixMerchantFeeProfile.feeProfileType === FeeProfileType.PAYMENT_TYPE
            ? office.FinixMerchantFeeProfile.cnpPercFee
            : office.FinixMerchantFeeProfile.creditCardPercFee
        );
      }
    }
  }, [office, setValue]);

  useEffect(() => {
    if (card) {
      setIsACHPayment(card.type === "BANK_ACCOUNT");
    }
  }, [card, paymentOption]);

  const defaultPaymentTypeId = getDefaultPmsPaymentTypeId({
    office,
    isVirtual: true,
    isACH: isACHPayment,
  });

  useEffect(() => {
    if (showPaymentTypeSelect && defaultPaymentTypeId) {
      setValue("pmsPaymentTypeId", defaultPaymentTypeId);
    }
  }, [showPaymentTypeSelect, defaultPaymentTypeId, setValue]);

  const processVirtualPayment = trpc.viewer.patients.processVirtualPayment.useMutation({
    onSuccess: ({ amount, surchargeApplied }) => {
      processingRef.current = false;
      setProcessedAmount(amount);
      setFeeCharged(surchargeApplied);
      setLoading(false);
      showToast("Success", "success");
      setPaymentSuccessDialogOpen(true);
    },
    onError: (e) => {
      showToast(e.message || "Error", "error");
      setLoading(false);
      processingRef.current = false;
    },
  });

  const allocationsBalanced = useMemo(() => {
    if (!splitEnabled || familyMembers.length <= 1) return true;
    const totalCents = Math.round((parseFloat(watchAmount) || 0) * 100);
    const allocatedCents = allocations.reduce((sum, a) => sum + Math.round(a.amount * 100), 0);
    return totalCents === allocatedCents && allocations.length > 0;
  }, [splitEnabled, familyMembers.length, watchAmount, allocations]);

  const getAllocationsPayload = () =>
    splitEnabled && allocations.length > 0
      ? allocations.filter((a) => a.amount > 0).map((a) => ({ patientId: a.patientId, amount: a.amount }))
      : undefined;

  const ensureAllocationValid = () => {
    if (splitEnabled && !allocationsBalanced) {
      showToast("Family allocation must equal the payment amount", "error");
      return false;
    }
    return true;
  };

  const handleSubmit = (data: FormValues) => {
    if (processingRef.current) return;
    if (!patient) {
      showToast("Please select a patient", "error");
      return;
    }
    if (!ensureAllocationValid()) return;
    if (loading) return;
    processingRef.current = true;
    setLoading(true);
    const allocationsPayload = getAllocationsPayload();
    processVirtualPayment.mutate({
      patientId: patient,
      officeId,
      ...data,
      amount: +data.amount,
      enableSurcharge: data.enableSurcharge,
      isNewMethod: false,
      allocations: allocationsPayload,
      overridePaymentTypeId: showPaymentTypeSelect ? data.pmsPaymentTypeId : undefined,
    });
  };

  const handleFinixPayment = async (token: string) => {
    const validAmount = await trigger("amount");
    if (!validAmount) return;
    if (!ensureAllocationValid()) return;
    setLoading(true);

    processVirtualPayment.mutate({
      patientId: patient || undefined,
      officeId,
      amount: +getValues("amount"),
      enableSurcharge: getValues("enableSurcharge"),
      note: getValues("note"),
      token,
      isNewMethod: true,
      allocations: getAllocationsPayload(),
      overridePaymentTypeId: showPaymentTypeSelect ? getValues("pmsPaymentTypeId") : undefined,
    });
  };

  const handlePayrixPayment = async (tokenId: string) => {
    const validAmount = await trigger("amount");
    setLoading(false);
    if (!validAmount) return;
    if (!ensureAllocationValid()) return;
    setLoading(true);
    processVirtualPayment.mutate({
      patientId: patient || undefined,
      officeId,
      amount: +getValues("amount"),
      note: getValues("note"),
      tokenId,
      enableSurcharge: getValues("enableSurcharge"),
      isNewMethod: true,
      allocations: getAllocationsPayload(),
      overridePaymentTypeId: showPaymentTypeSelect ? getValues("pmsPaymentTypeId") : undefined,
    });
  };

  const resetFormState = () => {
    setPatient(undefined);
    setSplitEnabled(false);
    setAllocations([]);
    reset();
    if (showPaymentTypeSelect && defaultPaymentTypeId) {
      setValue("pmsPaymentTypeId", defaultPaymentTypeId);
    }
  };

  const allocationBlocksSubmit = splitEnabled && !allocationsBalanced;

  return (
    <div className="rounded-md bg-white text-sm text-gray-500">
      <Form form={form} handleSubmit={handleSubmit}>
        <div className="mt-6 flex flex-col gap-5">
          <div id="create-payment-account-select">
            <Label>Account(Optional)</Label>
            <OfficePatientSelect
              officeId={officeId}
              value={patient}
              onChange={(id) => {
                setPatient(id);
                if (id !== undefined) setValue("patientId", id);
              }}
              placeholder="Search Accounts"
              className="relative rounded-xl"
              showMissingPatientHelp
            />
          </div>
          {showPaymentTypeSelect && (
            <div id="create-payment-type-select">
              <NexhealthPaymentTypeSelect
                officeId={officeId}
                name="pmsPaymentTypeId"
                control={form.control}
                instanceId="virtual-nexhealth-payment-type"
              />
            </div>
          )}
          <div id="create-payment-amount-input">
            <Label>Amount</Label>
            <Controller
              name="amount"
              control={form.control}
              render={({ field: { value, onChange, onBlur, ...field } }) => (
                <TextField
                  className="relative rounded-lg text-gray-900"
                  label=""
                  step={0.01}
                  addOnLeading={<DollarIcon />}
                  {...field}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  onBlur={() => {
                    const formatted = formatAmountToTwoDecimals(value);
                    if (formatted !== value) onChange(formatted);
                    onBlur();
                  }}
                />
              )}
            />
          </div>
          {office?.enableFamilyAllocation && familyMembers.length > 1 && (
            <FamilyPaymentAllocation
              members={familyMembers}
              totalAmount={watchAmount}
              enabled={splitEnabled}
              onEnabledChange={setSplitEnabled}
              allocations={allocations}
              onAllocationsChange={setAllocations}
            />
          )}
          {(office?.applySurchargeFee || office?.enableDualPricing) && (
            <div id="create-payment-surcharge-switch" className="md:col-span-2">
              <Controller
                name="enableSurcharge"
                control={form.control}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    label={`${
                      office.applySurchargeFee ? "Enable Surcharge" : "Enable Dual Pricing"
                    }: Customers pay ${Math.round(feeRate * 10000) / 100}% for payments`}
                    checked={value}
                    onCheckedChange={onChange}
                    disabled={
                      (office.applySurchargeFee &&
                        (isACHPayment || (card && card?.cardType?.toLowerCase() !== "credit"))) ||
                      (office.enableDualPricing && isACHPayment)
                    }
                  />
                )}
              />
            </div>
          )}
          <div id="create-payment-note-textarea" className="md:col-span-1">
            <Label>Note</Label>
            <TextAreaField
              className="rounded-lg text-gray-900"
              placeholder="Note will be included with transaction details."
              rows={3}
              {...register("note")}
              label=""
            />
          </div>
          <div className="border-b border-gray-200" />
          <div id="create-payment-method-radio-group" className="space-y-2">
            <p className="text-base font-medium text-gray-900">Payment Method</p>
          </div>
          <RadioGroup.Root
            className="space-y-2"
            defaultValue={paymentOption}
            value={paymentOption}
            onValueChange={(value) => {
              setPaymentOption(value);
            }}>
            <div
              id="create-payment-method-radio-group-content"
              ref={animationParentRef}
              className="space-y-6">
              <div id="create-payment-method-radio-group-content-add-new" className="flex items-center">
                <RadioGroup.Item
                  id="add-new"
                  value="add-new"
                  className="min-w-4 flex h-4 w-4 cursor-pointer items-center rounded-full border border-blue-500 bg-white focus:border-2 focus:outline-none ltr:mr-2 rtl:ml-2">
                  <RadioGroup.Indicator className="relative flex h-4 w-4 items-center justify-center after:block after:h-2 after:w-2 after:rounded-full after:bg-blue-500" />
                </RadioGroup.Item>
                <Label htmlFor="add-new" className="!m-0 flex items-center">
                  Manually enter payment information
                </Label>
              </div>
              {paymentOption === "add-new" && (
                <div id="create-payment-method-radio-group-content-add-new-form" className="pl-6">
                  {office?.paymentGateway === PaymentGateway.FINIX && (
                    <PaymentForm
                      appId={appId}
                      env={env}
                      buttonBackColor="#111827"
                      buttonText="Process Payment"
                      handlePayment={handleFinixPayment}
                      isPaymentProcessing={loading}
                      enableACH={office.enableACHOnVirtual}
                      setIsACHPayment={setIsACHPayment}
                    />
                  )}
                  {office?.paymentGateway === PaymentGateway.PAYRIX && (
                    <PayrixForm
                      apiKey={payrixApiKey}
                      environment={payrixPaymentEnv}
                      merchant={office.merchantId || ""}
                      buttonText="Process Balance"
                      onSuccess={handlePayrixPayment}
                      paymentProcessing={loading}
                      setPaymentError={(err: string) => {
                        showToast(err, "error");
                      }}
                      setPaymentProcessing={setLoading}
                      enableACH={office.enableACHOnVirtual}
                      setIsACHPayment={setIsACHPayment}
                      disabled={watchAmount === "0.00" || allocationBlocksSubmit}
                    />
                  )}
                </div>
              )}
              {patient && (
                <div
                  id="create-payment-method-radio-group-content-use-existing"
                  className="flex items-center">
                  <RadioGroup.Item
                    id="use-existing"
                    value="use-existing"
                    className="min-w-4 flex h-4 w-4 cursor-pointer items-center rounded-full border border-blue-500 bg-white focus:border-2 focus:outline-none ltr:mr-2 rtl:ml-2">
                    <RadioGroup.Indicator className="relative flex h-4 w-4 items-center justify-center after:block after:h-2 after:w-2 after:rounded-full after:bg-blue-500" />
                  </RadioGroup.Item>
                  <Label htmlFor="use-existing" className="!m-0 flex items-center">
                    Use payment method on file
                  </Label>
                </div>
              )}
              {paymentOption === "use-existing" && (
                <div className="">
                  <div className="w-full pl-6">
                    <div className="flex w-full gap-2">
                      <PatientStoredPaymentMethodSelect
                        officeId={officeId}
                        patientId={patient}
                        value={card?.id}
                        showDefaultInLabel
                        onCardChange={(c) => {
                          setCard(c);
                          if (c) setValue("cardId", c.id);
                        }}
                      />
                    </div>
                    {formState.errors.cardId && (
                      <div className="text-gray mt-2 flex items-center gap-x-2 text-sm text-red-700">
                        <div>
                          <AlertIcon />
                        </div>
                        <p>Required</p>
                      </div>
                    )}
                  </div>

                  <div className="pl-6">
                    <Button
                      className="hover:bg-balck mt-4 flex h-10 w-full justify-center bg-black text-white hover:text-white"
                      type="submit"
                      loadingTextClassName="text-black"
                      loading={loading}
                      disabled={allocationBlocksSubmit}>
                      Process Payment
                    </Button>
                  </div>
                </div>
              )}
              {office?.applySurchargeFee && (
                <p className="pl-6 leading-6">
                  If a debit card is used for this transaction, the patient will not incur a surcharge.
                </p>
              )}
            </div>
          </RadioGroup.Root>
        </div>
      </Form>

      <PaymentSuccessDialog
        title="Payment successful"
        description="Thanks for your payment, your payment was successful."
        amount={processedAmount}
        account={singlePatient?.firstName || "" + " " + (singlePatient?.lastName || "")}
        date={new Date().toLocaleDateString()}
        source="Virtual Payment"
        open={paymentSuccessDialogOpen}
        handler={() => {
          setPaymentSuccessDialogOpen(false);
          resetFormState();
        }}
        onClose={() => {
          setPaymentSuccessDialogOpen(false);
          setPaymentDialog(false);
          resetFormState();
        }}
        email={singlePatient?.email || ""}
        phone={singlePatient?.phoneNumbers[0]?.number || ""}
        feeCharged={feeCharged}
      />
    </div>
  );
}
